import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { AlertTriangle, FileText, X, Clock } from 'lucide-react';
import { TextKeypad } from '@/components/ui/keypads';
import { VoucherListProps, CancelModalProps } from './VoucherList.types';
import { VoucherSummary } from '../../types';

const CancelModal: FC<CancelModalProps> = ({
  isOpen,
  voucherInfo,
  onClose,
  onConfirm,
  loading = false
}) => {
  const [reason, setReason] = useState('');
  const [textKeypadOpen, setTextKeypadOpen] = useState(false);

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason('');
    }
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  if (!voucherInfo) return null;

  const maxDays = voucherInfo.voucherType === 'boleta' ? 30 : 3;
  const isUrgent = (voucherInfo.daysRemaining ?? 0) <= 1;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Anular Comprobante
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium mb-2">Comprobante a Anular</h4>
              <p className="text-sm text-gray-600">
                {voucherInfo.voucherType === 'boleta' ? 'Boleta' : 'Factura'}: {voucherInfo.series}-{voucherInfo.number}
              </p>
              {voucherInfo.daysRemaining !== undefined && (
                <div className={`flex items-center gap-1 mt-2 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`}>
                  <Clock className="h-4 w-4" />
                  <span className="text-sm">
                    {voucherInfo.daysRemaining} días restantes de {maxDays} permitidos
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Motivo de Anulación *
              </label>
              <input
                type="text"
                value={reason}
                onClick={() => setTextKeypadOpen(true)}
                readOnly
                placeholder="Toca para escribir el motivo..."
                className="w-full px-3 py-3 border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Importante:</strong> La anulación será enviada a SUNAT a través de Nubefact.
                Este proceso no se puede deshacer.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-4">
            <Button variant="outline" onClick={handleClose} disabled={loading} className="flex-1 max-w-[220px] py-6 text-lg font-semibold">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!reason.trim() || loading}
              className="flex-1 max-w-[220px] py-6 text-lg font-semibold bg-red-600 hover:bg-red-700"
            >
              {loading ? 'Anulando...' : 'Confirmar Anulación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TextKeypad
        isOpen={textKeypadOpen}
        onClose={() => setTextKeypadOpen(false)}
        onSubmit={(value: string) => {
          setReason(value);
          setTextKeypadOpen(false);
        }}
        title="Motivo de Anulación"
        initialValue={reason}
        placeholder="MOTIVO DE ANULACIÓN"
      />
    </>
  );
};

export const VoucherList: FC<VoucherListProps> = ({ vouchers, onCancel, loading = false }) => {
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    voucherInfo: any;
  }>({
    isOpen: false,
    voucherInfo: null
  });

  const handleCancelClick = (voucher: VoucherSummary) => {
    setCancelModal({
      isOpen: true,
      voucherInfo: {
        id: voucher.id,
        series: voucher.series,
        number: voucher.number,
        voucherType: voucher.voucherType,
        daysRemaining: voucher.daysRemaining
      }
    });
  };

  const handleConfirmCancel = (reason: string) => {
    if (cancelModal.voucherInfo) {
      onCancel(cancelModal.voucherInfo.id, reason);
      setCancelModal({ isOpen: false, voucherInfo: null });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'emitido':
        return <Badge className="bg-green-100 text-green-800">Emitido</Badge>;
      case 'anulado':
        return <Badge className="bg-red-100 text-red-800">Anulado</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getVoucherTypeLabel = (type: string) => {
    return type === 'boleta' ? 'Boleta' : 'Factura';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dateFormatted = date.toLocaleDateString('es-PE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const timeFormatted = `${hours}:${minutes}`;
    return `${dateFormatted} ${timeFormatted}`;
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-macartur-primary mx-auto"></div>
          <p className="text-gray-600 mt-2">Cargando comprobantes...</p>
        </div>
      </Card>
    );
  }

  if (vouchers.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron comprobantes</h3>
          <p className="text-gray-500">No hay comprobantes que coincidan con los criterios de búsqueda.</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">
          Comprobantes Encontrados ({vouchers.length})
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Número
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Local
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {vouchers.map((voucher) => (
                <tr key={voucher.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {getVoucherTypeLabel(voucher.voucherType)}
                      </div>
                      {voucher.canCancel && voucher.daysRemaining !== undefined && voucher.daysRemaining <= 3 && (
                        <div className="flex items-center mt-1">
                          <Badge className="bg-orange-100 text-orange-800 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {voucher.daysRemaining}d restantes
                          </Badge>
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {voucher.series}-{voucher.number}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {voucher.customerName}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {voucher.locationName}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {formatDate(voucher.issueDate)}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm font-semibold text-macartur-primary">
                      S/ {(typeof voucher.total === 'number' ? voucher.total : parseFloat(voucher.total) || 0).toFixed(2)}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    {getStatusBadge(voucher.status)}
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="flex gap-2 justify-center">
                      {voucher.canCancel && voucher.status === 'emitido' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelClick(voucher)}
                          className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-3 h-3 mr-1" />
                          Anular
                        </Button>
                      )}
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-xs"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Ver PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <CancelModal
        isOpen={cancelModal.isOpen}
        voucherInfo={cancelModal.voucherInfo}
        onClose={() => setCancelModal({ isOpen: false, voucherInfo: null })}
        onConfirm={handleConfirmCancel}
        loading={loading}
      />
    </>
  );
};